import pluralize from "pluralize"
import PropTypes from "prop-types"
import React, { useState } from "react"

import Button from "src/components/Button"

const BulkMessageConfirm = ({
  message,
  recipients,
  onBack,
  onSend,
  isSending,
}) => {
  const [showValidList, setShowValidList] = useState(false)
  const [showInvalidList, setShowInvalidList] = useState(false)

  const { validRecipients, invalidRecipients, totalValid, totalInvalid } =
    recipients

  return (
    <div className="space-y-4">
      <div>
        <h4 className="mb-2 font-medium">Message Preview</h4>
        <div className="bg-gray-50 whitespace-pre-wrap rounded border p-3 text-sm">
          {message}
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between">
            <span className="text-green-700 font-medium">
              {totalValid} valid {pluralize("recipient", totalValid)}
            </span>
            {totalValid > 0 && (
              <Button
                variant="link"
                small
                onClick={() => setShowValidList(!showValidList)}
              >
                {showValidList ? "Hide" : "Show"}
              </Button>
            )}
          </div>

          {showValidList && (
            <div className="mt-2 max-h-40 overflow-y-auto rounded border">
              {validRecipients.map((r, index) => (
                <div
                  key={r.id}
                  className={`flex items-center justify-between px-3 py-2 text-sm ${
                    index !== validRecipients.length - 1 ? "border-b" : ""
                  }`}
                >
                  <div>
                    <span className="font-medium">
                      {r.contactName || "Unknown"}
                    </span>
                    {r.boatName && (
                      <span className="text-gray-500"> {r.boatName}</span>
                    )}
                  </div>
                  {r.email && (
                    <span
                      className="max-w-[180px] truncate text-gray-500"
                      title={r.email}
                    >
                      {r.email}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {totalInvalid > 0 && (
          <div>
            <div className="flex items-center justify-between">
              <span className="font-medium text-yellow-700">
                {totalInvalid} {pluralize("recipient", totalInvalid)} cannot
                receive messages
              </span>
              <Button
                variant="link"
                small
                onClick={() => setShowInvalidList(!showInvalidList)}
              >
                {showInvalidList ? "Hide" : "Show"}
              </Button>
            </div>

            {showInvalidList && (
              <div className="mt-2 max-h-40 overflow-y-auto rounded border">
                {invalidRecipients.map((r, index) => (
                  <div
                    key={r.id}
                    className={`flex items-center justify-between px-3 py-2 text-sm ${
                      index !== invalidRecipients.length - 1 ? "border-b" : ""
                    }`}
                  >
                    <div>
                      <span className="font-medium">
                        {r.contactName || "Unknown"}
                      </span>
                      {r.boatName && (
                        <span className="text-gray-500"> {r.boatName}</span>
                      )}
                    </div>
                    <span className="text-yellow-700">{r.reason}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-between pt-2">
        <Button variant="tertiary" onClick={onBack}>
          Back
        </Button>
        <Button
          variant="primary"
          onClick={onSend}
          disabled={isSending || totalValid === 0}
        >
          {isSending
            ? "Sending..."
            : `Send to ${totalValid} ${pluralize("recipient", totalValid)}`}
        </Button>
      </div>
    </div>
  )
}

BulkMessageConfirm.propTypes = {
  message: PropTypes.string.isRequired,
  recipients: PropTypes.shape({
    validRecipients: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        contactName: PropTypes.string,
        boatName: PropTypes.string,
        email: PropTypes.string,
      })
    ).isRequired,
    invalidRecipients: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        contactName: PropTypes.string,
        boatName: PropTypes.string,
        reason: PropTypes.string,
      })
    ).isRequired,
    totalValid: PropTypes.number.isRequired,
    totalInvalid: PropTypes.number.isRequired,
  }).isRequired,
  onBack: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  isSending: PropTypes.bool,
}

export default BulkMessageConfirm
